import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {priceDecimals} from '../helpers/PriceHelper';
import {COUPON_STATUS_SUCCESS, COUPON_TYPE_PERCENT, COUPON_TYPE_PRICE} from '../actions/CouponActions';

// Apply an upstream-validated coupon to a raw subtotal. Returns the
// discount amount (positive number). The shape of `coupon.data` is
// owned by the upstream `customer-api/coupon/check` response and is
// shared across all order forms (essay, resume, transcription).
function calculateDiscount(subtotal, coupon) {
    if (!coupon || coupon.status !== COUPON_STATUS_SUCCESS || !coupon.data || !coupon.data.isValid) {
        return 0;
    }
    const raw = parseFloat(coupon.data.discount);
    if (!raw || raw <= 0) return 0;

    if (Number(coupon.data.type) === COUPON_TYPE_PERCENT) {
        return subtotal * (raw / 100);
    }
    if (Number(coupon.data.type) === COUPON_TYPE_PRICE) {
        return Math.min(raw, subtotal);
    }
    return 0;
}

export default function useResumeCalculator(values, {useBalance = false, customerBalance = 0} = {}) {
    const {data: options} = useSelector(state => state.resumeOptions);
    const coupon = useSelector(state => state.coupon);

    return useMemo(() => {
        if (!options || !values) {
            return {packagePrice: 0, services: {}, features: {}, subtotal: 0, discount: 0, totalPrice: 0, coupon, balanceApplied: 0};
        }

        const {careerLevel, urgency, services: selectedServices = [], features: selectedFeatures = []} = values;
        const prices = options.price || {};

        const packagePrice = (prices[careerLevel] && prices[careerLevel][urgency])
            ? prices[careerLevel][urgency]
            : 0;

        const services = {};
        if (options.service) {
            Object.entries(options.service).forEach(([key, svc]) => {
                services[key] = {...svc, price: priceDecimals(packagePrice * svc.mod)};
            });
        }

        const features = {};
        if (options.features) {
            Object.entries(options.features).forEach(([key, feat]) => {
                features[key] = {...feat, price: priceDecimals(packagePrice * feat.mod)};
            });
        }

        let subtotal = 0;
        selectedServices.forEach(key => {
            if (services[key]) subtotal += parseFloat(services[key].price);
        });
        selectedFeatures.forEach(key => {
            if (features[key]) subtotal += parseFloat(features[key].price);
        });

        const discount = calculateDiscount(subtotal, coupon);
        const afterCoupon = Math.max(0, subtotal - discount);

        // Store credit caps at the post-coupon total — never overdraw what
        // the customer actually owes. Backend mirrors this rule, this is
        // just so the UI reflects the same number.
        const balanceApplied = useBalance && customerBalance > 0
            ? Math.min(parseFloat(customerBalance) || 0, afterCoupon)
            : 0;
        const totalPrice = Math.max(0, afterCoupon - balanceApplied);

        return {packagePrice, services, features, subtotal, discount, totalPrice, coupon, balanceApplied};
    }, [options, values, coupon, useBalance, customerBalance]);
}
